import React, { useState } from 'react'
// hooks
import { useModalData } from 'hooks/index'
import { useMutation } from '@apollo/client/react'
// components
import { Modal } from 'components/UI'
import { Header } from 'components/Modals/ScaleOptionModal/Create'
import { CloseIcon } from 'components/UI/Icons'
import { Button, BUTTON_LAYOUTS } from 'components/UI'
import SetupStep from './components/SetupStep'
import ConfirmStep from './components/ConfirmStep'
import LoaderWithOverlay from '../../UI/Loader/LoaderWithOverlay'
// constants
import { Modals } from 'constants/modals'
import { PAPER_PROPS } from './constants'
// graphql
import { AUTH_TWO_FACTOR_SETUP_MUTATION } from 'graphql/auth/twoFactor/mutations'
// views
import { ModalContent, StepTitle, IntroContainer, IntroText } from './views'

type Step = 'intro' | 'setup' | 'confirm'

const TwoFactorSetupModal: React.FC = () => {
  const {
    hideModal,
    modalData: { isOpen, refetch, setupToken, onSuccess }
  } = useModalData({ modalType: Modals.TWO_FACTOR_SETUP_MODAL })

  const [step, setStep] = useState<Step>('setup')
  const [setupData, setSetupData] = useState<{
    otpauthUri: string
    backupCodes: string[]
  } | null>(null)

  const mutationContext = setupToken ? { headers: { Authorization: `Bearer ${setupToken}` } } : undefined

  const [setupMutation, { loading }] = useMutation(AUTH_TWO_FACTOR_SETUP_MUTATION, {
    context: mutationContext,
    onCompleted: ({ authTwoFactorSetup: { data } }) => {
      setSetupData(data)
      setStep('setup')
    }
  })

  const handleStartSetup = () => {
    setStep('setup')
    setupMutation()
  }

  const handleConfirmComplete = () => {
    if (refetch) {
      refetch()
    }

    if (onSuccess) {
      onSuccess()
    }

    hideModal()
  }

  const handleClose = () => {
    setStep('setup')
    setSetupData(null)
    hideModal()
  }

  React.useEffect(() => {
    if (isOpen) {
      if (setupToken) {
        setStep('intro')
      } else {
        setupMutation()
      }
    }
  }, [isOpen])

  return (
    <Modal width='md' isOpen={isOpen} hideModal={handleClose} PaperProps={PAPER_PROPS}>
      <Header>
        <span>Enable Two-Factor Authentication</span>
        <CloseIcon right={0} onClick={handleClose} />
      </Header>

      <ModalContent>
        {step === 'intro' && (
          <IntroContainer>
            <StepTitle>Two-Factor Authentication Required</StepTitle>
            <IntroText>
              Your organization requires two-factor authentication (2FA) to be enabled before you can access your
              account. Please set it up now using an authenticator app.
            </IntroText>
            <Button layout={BUTTON_LAYOUTS.PURPLE} onClick={handleStartSetup}>
              Enable 2FA
            </Button>
          </IntroContainer>
        )}

        {loading && <LoaderWithOverlay isLoading height='800px' />}

        {!loading && setupData && (
          <>
            {step === 'setup' && (
              <SetupStep
                otpauthUri={setupData.otpauthUri}
                backupCodes={setupData.backupCodes}
                onNext={() => setStep('confirm')}
              />
            )}

            {step === 'confirm' && (
              <ConfirmStep setupToken={setupToken} onSuccess={handleConfirmComplete} onBack={() => setStep('setup')} />
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  )
}

export default TwoFactorSetupModal
